import { supabase } from './supabaseClient';
import { assertSensorRegistrationAllowed, mapRegistrationError } from '../utils/subscriptionCapacity';

/**
 * Add a sensor to Supabase
 * Based on React Native addSensorFromQR implementation
 */
export const addSensor = async (deviceId, name, macAddress, serialNumber, options = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Ensure user profile exists
    try {
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert(
          {
            id: user.id,
            email: user.email,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'id' }
        );

      if (profileError) {
        console.warn('⚠️ Failed to ensure user profile (non-blocking):', profileError);
      }
    } catch (profileEnsureError) {
      console.warn('⚠️ Error ensuring user profile (non-blocking):', profileEnsureError);
    }

    // Detect device type from name
    const detectDeviceType = (sensorName) => {
      if (!sensorName) return undefined;
      const name = sensorName.toLowerCase();
      if (name.includes('ela') || name.includes('p t en')) return 'ela';
      if (name.includes('kbeacon') || name.includes('kbpro')) return 'kbeacon';
      if (name.includes('sigmawit') || name.includes('blogm') || name.includes('t201') || name.includes('t301')) return 'sigmawit';
      return undefined;
    };

    const sensorName = (name || `Sensor ${deviceId.slice(-6)}`).trim();
    const detectedType = options.deviceType || detectDeviceType(sensorName);

    // Format MAC address if provided
    let macForStorage = undefined;
    if (macAddress) {
      // Check if it's a UUID (not a MAC)
      const uuidPattern = /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/i;
      if (!uuidPattern.test(macAddress)) {
        // Format MAC if it's in compact format (12 hex chars without colons)
        if (/^[A-F0-9]{12}$/i.test(macAddress)) {
          macForStorage = macAddress.match(/.{2}/g)?.join(':').toUpperCase();
        } else if (/^([A-F0-9]{2}[:-]){5}[A-F0-9]{2}$/i.test(macAddress)) {
          macForStorage = macAddress.toUpperCase();
        } else {
          // Try to clean and format
          const cleaned = macAddress.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
          if (cleaned.length === 12) {
            macForStorage = cleaned.match(/.{2}/g)?.join(':');
          }
        }
      }
    }

    const advertisedName = options.advertisedName || name || sensorName;
    const elaNameKey = options.elaNameKey || (detectedType === 'ela' ? sensorName : undefined);
    const kbeaconMacKey = options.kbeaconMacKey || (detectedType === 'kbeacon' && macForStorage ? macForStorage.replace(/[:-]/g, '').toUpperCase().slice(0, 12) : undefined);
    const claimMedium = options.claimMedium || 'Manual';

    console.log('Adding sensor with metadata:', {
      deviceId,
      sensorName,
      macAddress,
      macForStorage,
      serialNumber,
      advertisedName,
      detectedType,
      elaNameKey,
      kbeaconMacKey,
      claimMedium,
    });

    // Check if sensor already exists
    let existingSensor = null;

    const { data: existingByDeviceId } = await supabase
      .from('sensors')
      .select('id, name, mac_address, serial_number, device_type, ela_name_key, kbeacon_mac_key, advertised_device_name, claim_medium')
      .eq('user_id', user.id)
      .eq('device_id', deviceId)
      .maybeSingle();

    if (existingByDeviceId) {
      existingSensor = existingByDeviceId;
    }

    if (!existingSensor && macForStorage) {
      const { data: existingByMac } = await supabase
        .from('sensors')
        .select('id, name, mac_address, serial_number, device_type, ela_name_key, kbeacon_mac_key, advertised_device_name, claim_medium')
        .eq('user_id', user.id)
        .eq('mac_address', macForStorage)
        .maybeSingle();

      if (existingByMac) {
        existingSensor = existingByMac;
      }
    }

    if (!existingSensor && serialNumber) {
      const { data: existingBySerial } = await supabase
        .from('sensors')
        .select('id, name, mac_address, serial_number, device_type, ela_name_key, kbeacon_mac_key, advertised_device_name, claim_medium')
        .eq('user_id', user.id)
        .eq('serial_number', serialNumber)
        .maybeSingle();

      if (existingBySerial) {
        existingSensor = existingBySerial;
      }
    }

    if (existingSensor) {
      console.log('Sensor already exists:', existingSensor);
      
      const updateData = {};

      if (sensorName && existingSensor.name !== sensorName) {
        updateData.name = sensorName;
      }

      if (macForStorage) {
        const existingMac = existingSensor.mac_address ? existingSensor.mac_address.toUpperCase() : undefined;
        if (!existingMac || existingMac !== macForStorage.toUpperCase()) {
          updateData.mac_address = macForStorage;
        }
      }

      if (serialNumber && (existingSensor.serial_number !== serialNumber || !existingSensor.serial_number)) {
        updateData.serial_number = serialNumber;
      }

      if (advertisedName && existingSensor.advertised_device_name !== advertisedName) {
        updateData.advertised_device_name = advertisedName;
      }

      if (detectedType && existingSensor.device_type !== detectedType) {
        updateData.device_type = detectedType;
      }

      if (detectedType === 'ela' && elaNameKey && existingSensor.ela_name_key !== elaNameKey) {
        updateData.ela_name_key = elaNameKey;
      }

      if (detectedType === 'kbeacon' && kbeaconMacKey && existingSensor.kbeacon_mac_key !== kbeaconMacKey) {
        updateData.kbeacon_mac_key = kbeaconMacKey;
      }

      if (claimMedium && existingSensor.claim_medium !== claimMedium) {
        updateData.claim_medium = claimMedium;
      }

      if (Object.keys(updateData).length > 0) {
        console.log('Updating existing sensor with improved metadata:', updateData);
        const { error: updateError } = await supabase
          .from('sensors')
          .update(updateData)
          .eq('id', existingSensor.id);

        if (updateError) {
          console.error('Error updating sensor:', updateError);
          throw updateError;
        }

        console.log('Sensor metadata updated successfully');
        return existingSensor;
      } else {
        console.log('Sensor already up to date:', existingSensor.name);
        throw new Error(`Sensor "${sensorName}" (${deviceId}) is already added to your account`);
      }
    }

    console.log('Inserting new sensor record');

    try {
      await assertSensorRegistrationAllowed();
    } catch (capacityError) {
      throw new Error(mapRegistrationError(capacityError, 'sensor'));
    }

    const sensorData = {
      user_id: user.id,
      name: sensorName,
      device_id: deviceId,
      temperature_unit: 'celsius', // Default, can be updated later
    };

    if (macForStorage) {
      sensorData.mac_address = macForStorage;
    }

    if (serialNumber) {
      sensorData.serial_number = serialNumber;
    }

    if (advertisedName) {
      sensorData.advertised_device_name = advertisedName;
    }

    if (detectedType) {
      sensorData.device_type = detectedType;
    }

    if (detectedType === 'ela' && elaNameKey) {
      sensorData.ela_name_key = elaNameKey;
    }

    if (detectedType === 'kbeacon' && kbeaconMacKey) {
      sensorData.kbeacon_mac_key = kbeaconMacKey;
    }

    if (claimMedium) {
      sensorData.claim_medium = claimMedium;
    }

    console.log('Sensor data to insert:', sensorData);
    
    const { data: insertedSensor, error: sensorError } = await supabase
      .from('sensors')
      .insert(sensorData)
      .select()
      .single();

    if (sensorError) {
      console.error('Database insert error:', sensorError);
      throw new Error(mapRegistrationError(sensorError, 'sensor'));
    }
    
    console.log('Sensor inserted successfully:', insertedSensor);
    return insertedSensor;
  } catch (error) {
    console.error('❌ Failed to add sensor:', error);
    throw error;
  }
};

/**
 * Delete a sensor from Supabase
 * Deletes the sensor and all associated sensor readings
 */
export const deleteSensor = async (sensorId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    console.log('🗑️ Deleting sensor:', sensorId);

    // Delete sensor readings first (due to foreign key constraint)
    const { error: readingsError } = await supabase
      .from('sensor_readings')
      .delete()
      .eq('sensor_id', sensorId);

    if (readingsError) {
      console.error('❌ Error deleting sensor readings:', readingsError);
      throw new Error('Failed to delete sensor readings');
    }

    console.log('✅ Sensor readings deleted successfully');

    // Delete the sensor
    const { error: sensorError } = await supabase
      .from('sensors')
      .delete()
      .eq('id', sensorId)
      .eq('user_id', user.id); // Ensure user owns the sensor

    if (sensorError) {
      console.error('❌ Error deleting sensor:', sensorError);
      throw new Error('Failed to delete sensor');
    }

    console.log('✅ Sensor deleted successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Failed to delete sensor:', error);
    throw error;
  }
};

export default { addSensor, deleteSensor };
